import {
    GridCell,
    GridColumn,
    GridColumnEditorBase,
    GridRow,
    ICellEditor,
    ICellEditorWithText,
    IValueControl
} from '@Common/Controls/SimpleGridLight';
import { safeDestroys } from '@Common/Core/Destroyable';

export interface IExternalEditorSettings {
    createControl: (cell: GridCell) => IValueControl;
    getText?: (cell: GridCell) => string;
}

const externalEditorDefaultSettings: Required<Omit<IExternalEditorSettings, 'createControl'>> = {
    getText: (cell: GridCell) => {
        return cell.value().toString();
    }
};

export class ColumnExternalEditor extends GridColumnEditorBase {
    private readonly setting: Required<IExternalEditorSettings>;

    constructor(setting: IExternalEditorSettings) {
        super();
        this.setting = _.defaults(setting, externalEditorDefaultSettings);
    }

    public getEditor(row: GridRow, column: GridColumn): ICellEditor {
        return new CellExternalEditor(row.getCell(column), this.setting);
    }
}

class CellExternalEditor implements ICellEditor, ICellEditorWithText {
    private control!: IValueControl;
    private readonly view: JQuery;
    private isView = true;

    constructor(private readonly cell: GridCell, private readonly setting: RequiredKeys<IExternalEditorSettings, 'getText'>) {
        this.view = $('<div>');
        this.view.appendTo(cell.tableCell());
        this.refresh();
    }

    private getCellText(): string {
        return this.setting.getText(this.cell);
    }

    public isInActiveMode(): boolean {
        if (this.control != null) {
            return this.control.isInActiveMode();
        }
        return false;
    }

    public beginEdit(preview = false): void {
        const tableCell = this.cell.tableCell();
        if (this.control == null) {
            this.control = this.setting.createControl(this.cell);
        }
        this.control.value(this.cell.value());
        if (this.isView) {
            this.view.detach();
            this.control.getControl().appendTo(tableCell);
        }
        this.control.adjust(tableCell.width());
        this.isView = false;
    }

    public endEdit(): void {
        this.isView = true;
        if (this.control != null) {
            this.control.getControl().detach();
            this.cell.value(this.control.value());
        }
        this.cell.tableCell().append(this.view);
        this.refresh();
    }

    public refresh(): void {
        if (this.isView) {
            this.view.text(this.getCellText());
        } else {
            const tableCell = this.cell.tableCell();
            this.control.value(this.cell.value());
            this.control.getControl().appendTo(tableCell);
            this.control.adjust(tableCell.width());
        }
    }

    public getText(): string {
        return this.getCellText();
    }

    public destroy(): void {
        safeDestroys(this.view);
    }
}
